/**
 * Filter Configurations
 *
 * Predefined filter options for inline column header filters.
 */

import { FilterOption } from './types';

/**
 * Appointment status options
 */
export const APPOINTMENT_STATUS_OPTIONS: FilterOption[] = [
  { value: 'proposed', label: 'Proposed' },
  { value: 'booked', label: 'Booked' },
  { value: 'arrived', label: 'Arrived' },
  { value: 'pending', label: 'On Hold' },
  { value: 'fulfilled', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'noshow', label: 'No Show' },
];

/**
 * Appointment/visit type options
 */
export const APPOINTMENT_TYPE_OPTIONS: FilterOption[] = [
  { value: 'consultation', label: 'Consultation' },
  { value: 'follow-up', label: 'Follow-up' },
  { value: 'routine-checkup', label: 'Routine Checkup' },
  { value: 'emergency', label: 'Emergency' },
  { value: 'vaccination', label: 'Vaccination' },
  { value: 'lab-test', label: 'Lab Test' },
];

/**
 * Patient status options
 */
export const PATIENT_STATUS_OPTIONS: FilterOption[] = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'deceased', label: 'Deceased' },
];

/**
 * Gender options
 */
export const GENDER_OPTIONS: FilterOption[] = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' },
];

/**
 * Practitioner status options
 */
export const PRACTITIONER_STATUS_OPTIONS: FilterOption[] = [
  { value: 'true', label: 'Active' },
  { value: 'false', label: 'Inactive' },
];

/**
 * Custom date range type for use with getDateRangeFromFilter
 */
export interface CustomDateRange {
  from?: Date;
  to?: Date;
}

/**
 * Helper function to get date range values based on filter selection
 * Supports preset values and custom date ranges
 */
export function getDateRangeFromFilter(
  filterValue: string,
  customRange?: CustomDateRange
): {
  dateStart?: string;
  dateEnd?: string;
} {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  switch (filterValue) {
    case 'today': {
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      return {
        dateStart: today.toISOString(),
        dateEnd: tomorrow.toISOString(),
      };
    }
    case 'week': {
      const dayOfWeek = today.getDay();
      const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
      const monday = new Date(today);
      monday.setDate(monday.getDate() + diffToMonday);
      const nextMonday = new Date(monday);
      nextMonday.setDate(nextMonday.getDate() + 7);
      return {
        dateStart: monday.toISOString(),
        dateEnd: nextMonday.toISOString(),
      };
    }
    case 'month': {
      const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
      return {
        dateStart: firstDay.toISOString(),
        dateEnd: lastDay.toISOString(),
      };
    }
    case 'custom': {
      if (customRange?.from) {
        // Set start to beginning of the 'from' day
        const startDate = new Date(customRange.from);
        startDate.setHours(0, 0, 0, 0);

        // Set end to end of the 'to' day, or end of 'from' day if no 'to'
        const endDate = customRange.to ? new Date(customRange.to) : new Date(customRange.from);
        endDate.setHours(23, 59, 59, 999);

        return {
          dateStart: startDate.toISOString(),
          dateEnd: endDate.toISOString(),
        };
      }
      return {};
    }
    default:
      return {};
  }
}

/**
 * Helper function to convert sort config to FHIR sort parameter
 */
export function getSortParam(
  sortKey: string | null,
  direction: 'asc' | 'desc' | null
): string | undefined {
  if (!sortKey || !direction) return undefined;
  return direction === 'desc' ? `-${sortKey}` : sortKey;
}
